import type { FinancialMetricMode } from 'partner360/entities/financialMetricMode'

import type { TnetReviewFinRow } from '../model/types'

type ReferenceMetricMode = 'net' | 'ndp'

type QuarterValueKey = keyof Pick<
  TnetReviewFinRow,
  | 'ActualsCYQ1Ndp'
  | 'ActualsCYQ2Ndp'
  | 'ActualsCYQ1Net'
  | 'ActualsCYQ2Net'
  | 'ActualsCYQ1TNet'
  | 'ActualsCYQ2TNet'
  | 'ActualsPYQ1Ndp'
  | 'ActualsPYQ2Ndp'
  | 'ActualsPYQ1Net'
  | 'ActualsPYQ2Net'
  | 'ActualsPYQ1TNet'
  | 'ActualsPYQ2TNet'
  | 'ActualsPYQ3Ndp'
  | 'ActualsPYQ4Ndp'
  | 'ActualsPYQ3Net'
  | 'ActualsPYQ4Net'
  | 'ActualsPYQ3TNet'
  | 'ActualsPYQ4TNet'
  | 'PlansCYQ1Ndp'
  | 'PlansCYQ2Ndp'
  | 'PlansCYQ1Net'
  | 'PlansCYQ2Net'
  | 'PlansCYQ1TNet'
  | 'PlansCYQ2TNet'
  | 'PlansCYQ3Ndp'
  | 'PlansCYQ4Ndp'
  | 'PlansCYQ3Net'
  | 'PlansCYQ4Net'
  | 'PlansCYQ3TNet'
  | 'PlansCYQ4TNet'
>

type MetricPeriodFields = {
  ndp: QuarterValueKey[]
  net: QuarterValueKey[]
  tnet: QuarterValueKey[]
}

type MetricPeriodTotals = {
  h1Plan: number | null
  previousH2Actuals: number | null
  h1Actuals: number | null
  h1YoYGrowth: number | null
  h2Plan: number | null
  h2YoYGrowth: number | null
}

export type TnetReviewMetrics = {
  planning: MetricPeriodTotals
  review: MetricPeriodTotals
  summaryGrowth: number | null
}

const REVIEW_FIELDS = {
  h1Plan: {
    ndp: ['PlansCYQ1Ndp', 'PlansCYQ2Ndp'],
    net: ['PlansCYQ1Net', 'PlansCYQ2Net'],
    tnet: ['PlansCYQ1TNet', 'PlansCYQ2TNet'],
  },
  h1Actuals: {
    ndp: ['ActualsCYQ1Ndp', 'ActualsCYQ2Ndp'],
    net: ['ActualsCYQ1Net', 'ActualsCYQ2Net'],
    tnet: ['ActualsCYQ1TNet', 'ActualsCYQ2TNet'],
  },
  h1PreviousActuals: {
    ndp: ['ActualsPYQ1Ndp', 'ActualsPYQ2Ndp'],
    net: ['ActualsPYQ1Net', 'ActualsPYQ2Net'],
    tnet: ['ActualsPYQ1TNet', 'ActualsPYQ2TNet'],
  },
  h2Plan: {
    ndp: ['PlansCYQ3Ndp', 'PlansCYQ4Ndp'],
    net: ['PlansCYQ3Net', 'PlansCYQ4Net'],
    tnet: ['PlansCYQ3TNet', 'PlansCYQ4TNet'],
  },
  h2PreviousActuals: {
    ndp: ['ActualsPYQ3Ndp', 'ActualsPYQ4Ndp'],
    net: ['ActualsPYQ3Net', 'ActualsPYQ4Net'],
    tnet: ['ActualsPYQ3TNet', 'ActualsPYQ4TNet'],
  },
} satisfies Record<string, MetricPeriodFields>

const getRowsForReview = (finRows: TnetReviewFinRow[]) => {
  const aggregatedRows = finRows.filter((row) => !row.Category?.trim())
  return aggregatedRows.length > 0 ? aggregatedRows : finRows
}

const sumFields = (row: TnetReviewFinRow, fields: QuarterValueKey[]) =>
  fields.reduce((sum, field) => sum + Number(row[field] ?? 0), 0)

const getReferenceMetricMode = (metricMode: FinancialMetricMode): ReferenceMetricMode =>
  metricMode === 'ndp' ? 'ndp' : 'net'

const buildPeriodValue = (
  rows: TnetReviewFinRow[],
  fields: MetricPeriodFields,
  metricMode: FinancialMetricMode,
  referenceMetricMode: ReferenceMetricMode
): number | null => {
  if (rows.length === 0) {
    return null
  }

  if (metricMode !== 'tnet') {
    return rows.reduce((sum, row) => sum + sumFields(row, fields[metricMode]), 0)
  }

  let total = 0
  let hasTnetValue = false
  let hasMissingTnetValue = false

  rows.forEach((row) => {
    const referenceValue = sumFields(row, fields[referenceMetricMode])
    const hasAllTnetValues = fields.tnet.every((field) => row[field] != null)

    if (hasAllTnetValues) {
      hasTnetValue = true
      total += sumFields(row, fields.tnet)
      return
    }

    if (referenceValue !== 0) {
      hasMissingTnetValue = true
    }
  })

  if (!hasTnetValue || hasMissingTnetValue) {
    return null
  }

  return total
}

const getGrowthValue = (previous: number | null, next: number | null) => {
  if (previous === null || next === null || previous === 0) {
    return null
  }

  return ((next - previous) / previous) * 100
}

const buildMetricPeriodTotals = (
  rows: TnetReviewFinRow[],
  metricMode: FinancialMetricMode,
  referenceMetricMode: ReferenceMetricMode
): MetricPeriodTotals => {
  const getValue = (fields: MetricPeriodFields) =>
    buildPeriodValue(rows, fields, metricMode, referenceMetricMode)

  const h1Plan = getValue(REVIEW_FIELDS.h1Plan)
  const previousH2Actuals = getValue(REVIEW_FIELDS.h2PreviousActuals)
  const h1Actuals = getValue(REVIEW_FIELDS.h1Actuals)
  const h1PreviousActuals = getValue(REVIEW_FIELDS.h1PreviousActuals)
  const h2Plan = getValue(REVIEW_FIELDS.h2Plan)
  const h2PreviousActuals = getValue(REVIEW_FIELDS.h2PreviousActuals)

  return {
    h1Plan,
    previousH2Actuals,
    h1Actuals,
    h1YoYGrowth: getGrowthValue(h1PreviousActuals, h1Actuals),
    h2Plan,
    h2YoYGrowth: getGrowthValue(h2PreviousActuals, h2Plan),
  }
}

export const buildTnetReviewMetrics = (
  finRows: TnetReviewFinRow[],
  metricMode: FinancialMetricMode,
  reviewMetricMode: FinancialMetricMode
): TnetReviewMetrics => {
  const reviewRows = getRowsForReview(finRows)
  const referenceMetricMode = getReferenceMetricMode(metricMode)
  const planning = buildMetricPeriodTotals(reviewRows, metricMode, referenceMetricMode)
  const review = buildMetricPeriodTotals(reviewRows, reviewMetricMode, referenceMetricMode)

  return {
    planning,
    review,
    summaryGrowth: review.h2YoYGrowth,
  }
}
